import SnapHelper from './snap_helper.js';

/**
 * Manages drag operations for canvas elements with snap-to-edge and snap-to-centerline support.
 * Handles single and multi-element dragging, applying snap adjustments to all dragged elements.
 */
export default class DragManager {
  /**
   * @param {HTMLElement} canvasElement - The canvas container element
   * @param {Object} centerlineElements - Object with vertical and horizontal centerline elements
   * @param {SnapLineManager} snapLineManager - Manager for displaying snap indicator lines
   */
  constructor(canvasElement, centerlineElements, snapLineManager) {
    this.canvasElement = canvasElement;
    this.centerlineVertical = centerlineElements.vertical;
    this.centerlineHorizontal = centerlineElements.horizontal;
    this.snapLineManager = snapLineManager;
    this.snapHelper = new SnapHelper(canvasElement);

    this.isDragging = false;
    this.hasMoved = false;
    this.startX = 0;
    this.startY = 0;
    this.startLeft = 0;
    this.startTop = 0;
    this.currentElement = null;

    this.draggedElements = [];
  }

  /**
   * Initiates a drag operation. Shows the grid and centerlines while dragging.
   * Builds snap targets from all non-dragged elements.
   *
   * @param {MouseEvent} event - The mousedown event
   * @param {HTMLElement} element - The primary element being dragged
   * @param {HTMLElement[]} additionalElements - Additional elements to drag together (for multi-select)
   */
  startDrag(event, element, additionalElements = []) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
    this.hasMoved = false;
    this.currentElement = element;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startLeft = element.offsetLeft;
    this.startTop = element.offsetTop;

    this.draggedElements = [
      { element, startLeft: element.offsetLeft, startTop: element.offsetTop }
    ];

    additionalElements.forEach(el => {
      if (el !== element) {
        this.draggedElements.push({
          element: el,
          startLeft: el.offsetLeft,
          startTop: el.offsetTop
        });
      }
    });

    const allElements = this.canvasElement.querySelectorAll('.canvas-element');
    const excludedSet = new Set(this.draggedElements.map(d => d.element));
    this.snapHelper.buildSnapTargets(allElements, excludedSet);

    this.canvasElement.classList.add('show-grid');
    this.centerlineVertical.classList.remove('hidden');
    this.centerlineHorizontal.classList.remove('hidden');
  }

  /**
   * Handles mouse movement during drag. Snapping is calculated based on the primary element,
   * then the same snap adjustment is applied to all dragged elements.
   * Positions are clamped to keep all elements within canvas bounds.
   *
   * @param {MouseEvent} event - The mousemove event
   */
  handleDragMove(event) {
    if (!this.isDragging || !this.currentElement) {
      return;
    }

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (deltaX !== 0 || deltaY !== 0) {
      this.hasMoved = true;
    }

    const primaryData = this.draggedElements[0];
    const newLeft = primaryData.startLeft + deltaX;
    const newTop = primaryData.startTop + deltaY;

    const snapResult = this.snapHelper.snap(newLeft, newTop, this.currentElement);

    const snapDeltaX = snapResult.left - newLeft;
    const snapDeltaY = snapResult.top - newTop;

    const boundaryClamp = this._calculateBoundaryClamp(deltaX + snapDeltaX, deltaY + snapDeltaY);

    this.draggedElements.forEach(({ element, startLeft, startTop }) => {
      element.style.left = (startLeft + deltaX + snapDeltaX + boundaryClamp.x) + 'px';
      element.style.top = (startTop + deltaY + snapDeltaY + boundaryClamp.y) + 'px';
    });

    this.snapLineManager.updateSnapLines(snapResult);
  }

  /** Calculates adjustment needed to keep all dragged
   * elements (including outlines) within canvas bounds. */
  _calculateBoundaryClamp(totalDeltaX, totalDeltaY) {
    const canvasWidth = this.canvasElement.scrollWidth;
    const canvasHeight = this.canvasElement.scrollHeight;
    const offset = SnapHelper.VISUAL_BOUNDARY_OFFSET;

    // Infinity/-Infinity ensure any real value becomes the initial min/max
    let minLeft = Infinity, minTop = Infinity;
    let maxRight = -Infinity, maxBottom = -Infinity;

    this.draggedElements.forEach(({ element, startLeft, startTop }) => {
      const newLeft = startLeft + totalDeltaX - offset;
      const newTop = startTop + totalDeltaY - offset;
      const newRight = startLeft + totalDeltaX + element.offsetWidth + offset;
      const newBottom = startTop + totalDeltaY + element.offsetHeight + offset;

      minLeft = Math.min(minLeft, newLeft);
      minTop = Math.min(minTop, newTop);
      maxRight = Math.max(maxRight, newRight);
      maxBottom = Math.max(maxBottom, newBottom);
    });

    let clampX = 0, clampY = 0;

    if (minLeft < 0) {
      clampX = -minLeft;
    } else if (maxRight > canvasWidth) {
      clampX = canvasWidth - maxRight;
    }

    if (minTop < 0) {
      clampY = -minTop;
    } else if (maxBottom > canvasHeight) {
      clampY = canvasHeight - maxBottom;
    }

    return { x: clampX, y: clampY };
  }

  /**
   * Ends the drag operation, hides visual guides, and clears snap targets.
   * @returns {boolean|null} Whether the element was actually moved, or null if no drag was active
   */
  endDrag() {
    if (!this.isDragging) return null;

    const hasMoved = this.hasMoved;

    this.isDragging = false;
    this.hasMoved = false;
    this.currentElement = null;
    this.draggedElements = [];

    this.snapHelper.clearSnapTargets();
    this.snapLineManager.hideAllSnapLines();

    this.canvasElement.classList.remove('show-grid');
    this.centerlineVertical.classList.add('hidden');
    this.centerlineHorizontal.classList.add('hidden');

    return hasMoved;
  }

  isDraggingActive() {
    return this.isDragging;
  }
}
